'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';

import { Eye, EyeOff, Leaf, Mail, Lock, User, Building2, Users, Copy, Check, ArrowRight } from 'lucide-react';

type LoginData = { email: string; password: string; remember: boolean };
type SignupData = { name: string; email: string; password: string; role: string; country: string };

type Role = 'pledger' | 'partner' | 'group_admin' | 'admin';

const demoCredentials: { role: string; email: string; password: string }[] = [
  { role: 'Pledger', email: 'amara.osei@platephorm.io', password: 'GreenPlanet2026' },
  { role: 'Partner', email: 'partner@ecobrands.io', password: 'EcoRewards2026' },
  { role: 'Group Admin', email: 'groupadmin@earthcircle.io', password: 'CircleImpact2026' },
  { role: 'Admin', email: 'admin@platephorm.io', password: 'AdminPledge2026' },
];

const roles: { id: Role; label: string; icon: React.ElementType; desc: string }[] = [
  { id: 'pledger', label: 'Pledger', icon: Leaf, desc: 'Take pledges & earn rewards' },
  { id: 'partner', label: 'Partner Business', icon: Building2, desc: 'List discount offers' },
  { id: 'group_admin', label: 'Group Admin', icon: Users, desc: 'Manage community groups' },
  { id: 'admin', label: 'Admin', icon: User, desc: 'Platform management' },
];

export default function AuthForm() {
  const [tab, setTab] = useState<'login' | 'signup'>('login');
  const [showPw, setShowPw] = useState(false);
  const [selectedRole, setSelectedRole] = useState<Role>('pledger');
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [loginLoading, setLoginLoading] = useState(false);
  const [signupLoading, setSignupLoading] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [loginSuccess, setLoginSuccess] = useState(false);

  const loginForm = useForm<LoginData>({ defaultValues: { email: '', password: '', remember: false } });
  const signupForm = useForm<SignupData>({ defaultValues: { name: '', email: '', password: '', role: 'pledger', country: '' } });

  const handleCopy = (text: string, field: string) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 1500);
  };

  const autofill = (email: string, password: string) => {
    loginForm.setValue('email', email);
    loginForm.setValue('password', password);
    setTab('login');
  };

  const onLogin = async (data: LoginData) => {
    setLoginLoading(true);
    setLoginError('');
    await new Promise((r) => setTimeout(r, 1200));
    const valid = demoCredentials.find((c) => c.email === data.email && c.password === data.password);
    if (!valid) {
      setLoginError('Invalid credentials — use the demo accounts below to sign in.');
      setLoginLoading(false);
      return;
    }
    setLoginSuccess(true);
    setLoginLoading(false);
    // Backend integration: POST /api/auth/login
    window.location.href = '/pledger-dashboard';
  };

  const onSignup = async (data: SignupData) => {
    setSignupLoading(true);
    await new Promise((r) => setTimeout(r, 1200));
    setSignupLoading(false);
    // Backend integration: POST /api/auth/signup
    window.location.href = '/pledger-dashboard';
  };

  return (
    <div className="flex-1 flex flex-col justify-center items-center px-6 py-10 bg-background">
      <div className="w-full max-w-md">
        {/* Mobile logo */}
        <div className="flex items-center gap-2 mb-8 lg:hidden">
          <Leaf size={28} className="text-primary" />
          <span className="font-extrabold text-primary text-xl">Platephorm</span>
        </div>

        <h2 className="text-2xl font-bold text-foreground mb-1">
          {tab === 'login' ? 'Welcome back' : 'Join the movement'}
        </h2>
        <p className="text-muted-foreground text-sm mb-6">
          {tab === 'login' ? 'Sign in to your Platephorm account' : 'Start pledging for the planet today'}
        </p>

        {/* Tabs */}
        <div className="flex bg-muted rounded-xl p-1 mb-6">
          {(['login', 'signup'] as const).map((t) => (
            <button
              key={`tab-${t}`}
              onClick={() => setTab(t)}
              className={`flex-1 py-2 rounded-lg text-sm font-semibold transition-all duration-200 ${
                tab === t ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {t === 'login' ? 'Sign In' : 'Sign Up'}
            </button>
          ))}
        </div>

        {/* Social Auth */}
        <div className="flex gap-3 mb-5">
          {[
            { label: 'Google', icon: '🌐' },
            { label: 'Apple', icon: '🍎' },
            { label: 'GitHub', icon: '🐙' },
          ].map((s) => (
            <button
              key={`social-${s.label}`}
              className="flex-1 flex items-center justify-center gap-2 border border-border rounded-xl py-2.5 text-sm font-medium text-foreground hover:bg-muted transition-all duration-150 active:scale-95"
            >
              <span>{s.icon}</span>
              <span>{s.label}</span>
            </button>
          ))}
        </div>

        <div className="flex items-center gap-3 mb-5">
          <div className="flex-1 h-px bg-border" />
          <span className="text-xs text-muted-foreground">or continue with email</span>
          <div className="flex-1 h-px bg-border" />
        </div>

        {/* LOGIN FORM */}
        {tab === 'login' && (
          <form onSubmit={loginForm.handleSubmit(onLogin)} className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-foreground mb-1.5">Email address</label>
              <div className="relative">
                <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  type="email"
                  placeholder="you@example.com"
                  className={`w-full pl-9 pr-4 py-2.5 bg-input border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-ring transition-all ${
                    loginForm.formState.errors.email ? 'border-negative' : 'border-border'
                  }`}
                  {...loginForm.register('email', { required: 'Email is required' })}
                />
              </div>
              {loginForm.formState.errors.email && (
                <p className="text-negative text-xs mt-1">{loginForm.formState.errors.email.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-semibold text-foreground mb-1.5">Password</label>
              <div className="relative">
                <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  type={showPw ? 'text' : 'password'}
                  placeholder="Your password"
                  className={`w-full pl-9 pr-10 py-2.5 bg-input border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-ring transition-all ${
                    loginForm.formState.errors.password ? 'border-negative' : 'border-border'
                  }`}
                  {...loginForm.register('password', { required: 'Password is required' })}
                />
                <button
                  type="button"
                  onClick={() => setShowPw(!showPw)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              {loginForm.formState.errors.password && (
                <p className="text-negative text-xs mt-1">{loginForm.formState.errors.password.message}</p>
              )}
            </div>

            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer">
                <input type="checkbox" {...loginForm.register('remember')} className="rounded" />
                Remember me
              </label>
              <button type="button" className="text-sm text-primary font-medium hover:underline">
                Forgot password?
              </button>
            </div>

            {loginError && (
              <div className="bg-negative/10 border border-negative/20 rounded-xl px-4 py-3">
                <p className="text-negative text-sm">{loginError}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={loginLoading}
              className="w-full gradient-green text-white font-semibold py-3 rounded-xl flex items-center justify-center gap-2 transition-all duration-150 active:scale-95 disabled:opacity-70"
              style={{ minHeight: 48 }}
            >
              {loginLoading ? (
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <>Sign In <ArrowRight size={16} /></>
              )}
            </button>
          </form>
        )}

        {/* SIGNUP FORM */}
        {tab === 'signup' && (
          <form onSubmit={signupForm.handleSubmit(onSignup)} className="space-y-4">
            {/* Role Selector */}
            <div>
              <label className="block text-sm font-semibold text-foreground mb-2">I am joining as</label>
              <div className="grid grid-cols-2 gap-2">
                {roles.map((r) => (
                  <button
                    key={`role-${r.id}`}
                    type="button"
                    onClick={() => setSelectedRole(r.id)}
                    className={`flex items-center gap-2 p-3 rounded-xl border text-left transition-all duration-150 ${
                      selectedRole === r.id
                        ? 'border-primary bg-primary/5 text-primary' :'border-border text-muted-foreground hover:border-primary/50'
                    }`}
                  >
                    <r.icon size={16} className="flex-shrink-0" />
                    <div>
                      <p className="text-xs font-semibold">{r.label}</p>
                      <p className="text-xs opacity-70">{r.desc}</p>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-foreground mb-1.5">Full name</label>
              <input
                type="text"
                placeholder="Your full name"
                className="w-full px-4 py-2.5 bg-input border border-border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                {...signupForm.register('name', { required: 'Name is required' })}
              />
              {signupForm.formState.errors.name && (
                <p className="text-negative text-xs mt-1">{signupForm.formState.errors.name.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-semibold text-foreground mb-1.5">Email address</label>
              <input
                type="email"
                placeholder="you@example.com"
                className="w-full px-4 py-2.5 bg-input border border-border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                {...signupForm.register('email', { required: 'Email is required' })}
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-foreground mb-1.5">Password</label>
              <div className="relative">
                <input
                  type={showPw ? 'text' : 'password'}
                  placeholder="Create a strong password"
                  className="w-full px-4 pr-10 py-2.5 bg-input border border-border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  {...signupForm.register('password', { required: 'Password required', minLength: { value: 8, message: 'Min 8 characters' } })}
                />
                <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                  {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              {signupForm.formState.errors.password && (
                <p className="text-negative text-xs mt-1">{signupForm.formState.errors.password.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-semibold text-foreground mb-1.5">Country</label>
              <select
                className="w-full px-4 py-2.5 bg-input border border-border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                {...signupForm.register('country', { required: 'Country is required' })}
              >
                <option value="">Select your country</option>
                <option value="GH">🇬🇭 Ghana</option>
                <option value="NG">🇳🇬 Nigeria</option>
                <option value="KE">🇰🇪 Kenya</option>
                <option value="ZA">🇿🇦 South Africa</option>
                <option value="BR">🇧🇷 Brazil</option>
                <option value="JP">🇯🇵 Japan</option>
                <option value="NL">🇳🇱 Netherlands</option>
                <option value="US">🇺🇸 United States</option>
                <option value="GB">🇬🇧 United Kingdom</option>
                <option value="IN">🇮🇳 India</option>
                <option value="AU">🇦🇺 Australia</option>
                <option value="DE">🇩🇪 Germany</option>
              </select>
            </div>

            <p className="text-xs text-muted-foreground">
              By signing up, you agree to our{' '}
              <button type="button" className="text-primary underline">Terms of Service</button> and{' '}
              <button type="button" className="text-primary underline">Privacy Policy</button>.
            </p>

            <button
              type="submit"
              disabled={signupLoading}
              className="w-full gradient-green text-white font-semibold py-3 rounded-xl flex items-center justify-center gap-2 transition-all duration-150 active:scale-95 disabled:opacity-70"
              style={{ minHeight: 48 }}
            >
              {signupLoading ? (
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <>Create Account <ArrowRight size={16} /></>
              )}
            </button>
          </form>
        )}

        {/* Demo Credentials */}
        <div className="mt-6 border border-border rounded-xl overflow-hidden">
          <div className="bg-muted px-4 py-2 flex items-center gap-2">
            <Leaf size={14} className="text-primary" />
            <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Demo Accounts</span>
          </div>
          <div className="divide-y divide-border">
            {demoCredentials.map((cred) => (
              <div key={`cred-${cred.role}`} className="flex items-center gap-3 px-4 py-2.5">
                <span className="text-xs font-semibold text-foreground w-20 flex-shrink-0">{cred.role}</span>
                <span className="text-xs text-muted-foreground flex-1 truncate font-mono">{cred.email}</span>
                <button
                  onClick={() => autofill(cred.email, cred.password)}
                  className="text-xs text-primary font-semibold hover:underline flex-shrink-0 px-2 py-1 bg-primary/5 rounded-lg transition-all"
                >
                  Use
                </button>
                <button
                  onClick={() => handleCopy(`${cred.email} / ${cred.password}`, cred.role)}
                  className="text-muted-foreground hover:text-foreground transition-all"
                >
                  {copiedField === cred.role ? <Check size={14} className="text-positive" /> : <Copy size={14} />}
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}